import { Box, Button, CircularProgress, Paper, Stack } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate, useLocation } from "react-router";
import DashboardHeader from "../../components/dashboard-header";
import { useFirebaseImageUpload } from "../../components/firebase-image-upload/use-firebase-image-upload";
import { GET_HOMEOWNER_MATCHING_PROFILE } from "../../graphql/homeowner-matching-profile";
import {
  CREATE_HOMEOWNER_BIO,
  GET_HOMEOWNER_BIO,
  UPDATE_HOMEOWNER_BIO,
} from "../../graphql/homeowner-bio";
import {
  CREATE_HOMEOWNER_PROJECT,
  GET_HOMEOWNER_PROJECTS,
  UPDATE_HOMEOWNER_PROJECT,
} from "../../graphql/homeowner-project";
import { useUserContext, useUserContextDispatch } from "../../hooks/userContext";
import { useDashboardFooter } from "../../contexts/DashboardFooterContext";
import { useNotification } from "../../contexts/NotificationContext";
import { Pages } from "../../utils";
import { FirebasePaths } from "../../utils/firebase";
import type { HomeOwnerUser } from "../../__generated__/types";
import BioHeaderSection from "./components/sections/BioHeaderSection";
import ProjectSection from "./components/sections/ProjectSection";
import WorkStyleSection from "./components/sections/WorkStyleSection";
import AdditionalDetailsSection from "./components/sections/AdditionalDetailsSection";
import ExperienceDialog from "./components/dialogs/ExperienceDialog";
import PetsDialog from "./components/dialogs/PetsDialog";
import HouseholdDialog from "./components/dialogs/HouseholdDialog";
import {
  OTHER_REASON_FOR_HIRING,
  PRIORITY_LABELS,
  normalizeReasonForHiring,
} from "./data";
import { CARD_BORDER, CARD_RADIUS, SECTION_BORDER, SECTION_RADIUS } from "./styles";
import {
  formatList,
  formatValue,
  isExperienceRating,
  ensureFutureDate,
  getTomorrowDate,
  parseCountValue,
  toTitleCase,
} from "./utils";
import type {
  ExperienceRating,
  HomeownerBioPageProps,
  HomeownerBioQueryData,
  HomeownerBioQueryVars,
  HomeownerProfileQueryData,
  HomeownerProfileQueryVars,
  HomeownerProjectRecord,
  HomeownerProjectsQueryData,
  HomeownerProjectsQueryVars,
  PetEntry,
} from "./types";

export const HomeownerBioPage = ({
  viewOnly = false,
  homeOwnerId,
  embedded = false,
  projectMode = "edit",
  initialProjectId = null,
  projectActiveFilter = true,
  onComplete,
}: HomeownerBioPageProps) => {
  const { setFooter, clearFooter } = useDashboardFooter();
  const { user } = useUserContext();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading: profileImageLoading, onUpload: uploadProfileImage } =
    useFirebaseImageUpload(FirebasePaths.HOMEOWNER_PROFILE_IMAGE);
  const { updateHomeownerAccountData } = useUserContextDispatch();
  const effectiveHomeOwnerId =
    homeOwnerId ??
    user?.id ??
    (user as { _id?: string | null })?._id ??
    "";
  const { data: matchingData } = useQuery<
    HomeownerProfileQueryData,
    HomeownerProfileQueryVars
  >(
    GET_HOMEOWNER_MATCHING_PROFILE,
    {
      variables: { homeOwnerId: effectiveHomeOwnerId },
      skip: !effectiveHomeOwnerId || embedded,
    }
  );
  const { data: bioData, refetch: refetchBio } = useQuery<
    HomeownerBioQueryData,
    HomeownerBioQueryVars
  >(GET_HOMEOWNER_BIO, {
    variables: { homeOwnerId: effectiveHomeOwnerId },
    skip: !effectiveHomeOwnerId || embedded,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });
  const activeFilter =
    projectActiveFilter === null ? undefined : projectActiveFilter;
  const { data: projectsData } = useQuery<
    HomeownerProjectsQueryData,
    HomeownerProjectsQueryVars
  >(GET_HOMEOWNER_PROJECTS, {
    variables: { homeOwnerId: effectiveHomeOwnerId, active: activeFilter },
    skip: !effectiveHomeOwnerId,
  });
  const [createHomeOwnerBioMutation, { loading: createBioLoading }] =
    useMutation(CREATE_HOMEOWNER_BIO);
  const [updateHomeOwnerBioMutation, { loading: updateBioLoading }] =
    useMutation(UPDATE_HOMEOWNER_BIO);
  const [createHomeOwnerProjectMutation, { loading: createProjectLoading }] =
    useMutation(CREATE_HOMEOWNER_PROJECT);
  const [updateHomeOwnerProjectMutation, { loading: updateProjectLoading }] =
    useMutation(UPDATE_HOMEOWNER_PROJECT);

  const homeownerUser = user as Partial<HomeOwnerUser> | null;

  const [bioId, setBioId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [profileImage, setProfileImage] = useState<{
    file?: File;
    local_url: string;
    _id?: string;
  } | null>(null);
  const [profileImageRemoved, setProfileImageRemoved] = useState(false);
  const [hireLikelihood, setHireLikelihood] = useState("");

  const [projectSelection, setProjectSelection] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectPropertyType, setProjectPropertyType] = useState("");
  const [projectScope, setProjectScope] = useState<string[]>([]);
  const [projectBillingPreferences, setProjectBillingPreferences] = useState<string[]>(
    []
  );
  const [projectBudgetRange, setProjectBudgetRange] = useState("");
  const [projectSpecialRequirements, setProjectSpecialRequirements] = useState<
    string[]
  >([]);
  const [projectFurniturePreferences, setProjectFurniturePreferences] = useState<
    string[]
  >([]);
  const [projectStartDate, setProjectStartDate] = useState<string | null>(
    "2024-10-01"
  );
  const [hardDeadline, setHardDeadline] = useState(false);
  const [deadlineDate, setDeadlineDate] = useState<string | null>("2024-10-15");
  const [deadlineDateWarning, setDeadlineDateWarning] = useState("");
  const [financing, setFinancing] = useState(false);
  const [reasonForHiring, setReasonForHiring] = useState("");
  const [otherReasonForHiring, setOtherReasonForHiring] = useState("");
  const [ownershipDuration, setOwnershipDuration] = useState("");
  const [propertyDetails, setPropertyDetails] = useState("");
  const [residenceType, setResidenceType] = useState("");
  const [propertyLocation, setPropertyLocation] = useState("");
  const [squareFootage, setSquareFootage] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");

  const [decisionMakers, setDecisionMakers] = useState("");
  const [worksRemotely, setWorksRemotely] = useState(false);
  const [workedWithDesigner, setWorkedWithDesigner] = useState(false);
  const [entertainRegularly, setEntertainRegularly] = useState(false);
  const [hasPets, setHasPets] = useState(false);
  const [hobbies, setHobbies] = useState<string[]>([]);

  const [experience, setExperience] = useState<{
    rating: ExperienceRating;
    notes: string;
  } | null>(null);
  const [experienceModalOpen, setExperienceModalOpen] = useState(false);
  const [experienceDraftRating, setExperienceDraftRating] =
    useState<ExperienceRating>("happy");
  const [experienceDraftNotes, setExperienceDraftNotes] = useState("");

  const [pets, setPets] = useState<PetEntry[]>([]);
  const [petsModalOpen, setPetsModalOpen] = useState(false);
  const [petDrafts, setPetDrafts] = useState<PetEntry[]>(pets);

  const [household, setHousehold] = useState<{
    adults: string;
    kids: string;
    kidsAges: string;
  } | null>(null);
  const [householdModalOpen, setHouseholdModalOpen] = useState(false);
  const [householdDraft, setHouseholdDraft] = useState({
    adults: "",
    kids: "",
    kidsAges: "",
  });

  const [wishList, setWishList] = useState<string[]>([]);
  const [newWishItem, setNewWishItem] = useState("");
  const [notes, setNotes] = useState("");
  const [designerNotes, setDesignerNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const hasHydratedBioRef = useRef(false);
  const hasHydratedProjectRef = useRef(false);
  const isAddMode = projectMode === "add";
  const isProjectModal = embedded;

  useEffect(() => {
    if (!isAddMode) {
      return;
    }
    setProjectId(null);
    setProjectSelection("");
    setProjectName("");
    setProjectPropertyType("");
    setProjectScope([]);
    setProjectBillingPreferences([]);
    setProjectBudgetRange("");
    setProjectSpecialRequirements([]);
    setProjectFurniturePreferences([]);
    setProjectStartDate(null);
    setHardDeadline(false);
    setDeadlineDate(null);
    setDeadlineDateWarning("");
    setFinancing(false);
    setReasonForHiring("");
    setOtherReasonForHiring("");
    setOwnershipDuration("");
    setPropertyDetails("");
    setResidenceType("");
    setPropertyLocation("");
    setSquareFootage("");
    setBedrooms("");
    setBathrooms("");
    setHireLikelihood("");
    setDecisionMakers("");
    setWorksRemotely(false);
    setWorkedWithDesigner(false);
    setExperience(null);
    setExperienceDraftRating("happy");
    setExperienceDraftNotes("");
    setExperienceModalOpen(false);
    setEntertainRegularly(false);
    setHasPets(false);
    setPets([]);
    setPetsModalOpen(false);
    setPetDrafts([]);
    setHobbies([]);
    setHousehold(null);
    setHouseholdModalOpen(false);
    setHouseholdDraft({ adults: "", kids: "", kidsAges: "" });
    setWishList([]);
    setNewWishItem("");
    setNotes("");
    setDesignerNotes("");
    setProfileImage(null);
    setProfilePreview(null);
    setProfileImageRemoved(false);
  }, [isAddMode]);

  useEffect(() => {
    if (!profilePreview || !profilePreview.startsWith("blob:")) {
      return;
    }
    return () => {
      URL.revokeObjectURL(profilePreview);
    };
  }, [profilePreview]);

  useEffect(() => {
    const bio = bioData?.getHomeOwnerBio;
    if (!bio || hasHydratedBioRef.current) {
      return;
    }
    setBioId(bio._id ?? null);
    if (isAddMode) {
      hasHydratedBioRef.current = true;
      return;
    }
    setHireLikelihood(bio.hireLikelihood ?? "");
    setDecisionMakers(bio.decisionMakers ?? "");
    setWorksRemotely(Boolean(bio.worksRemotely));
    setWorkedWithDesigner(Boolean(bio.workedWithDesigner));

    const normalizedRating = isExperienceRating(bio.experience?.rating)
      ? bio.experience?.rating
      : null;
    const normalizedNotes = bio.experience?.notes ?? "";
    if (normalizedRating || normalizedNotes.trim()) {
      setExperience({
        rating: normalizedRating ?? "neutral",
        notes: normalizedNotes,
      });
    } else {
      setExperience(null);
    }

    setEntertainRegularly(Boolean(bio.entertainRegularly));
    setHasPets(Boolean(bio.hasPets));

    const nextPets = (bio.pets ?? [])
      .map((pet) => ({
        type: pet.type ?? "",
        count: pet.count ?? "",
      }))
      .filter((pet) => pet.type || pet.count);
    setPets(bio.hasPets ? nextPets : []);
    setHobbies(bio.hobbies ?? []);

    const nextHousehold =
      bio.household &&
      (bio.household.adults || bio.household.kids || bio.household.kidsAges)
        ? {
            adults: bio.household.adults ?? "",
            kids: bio.household.kids ?? "",
            kidsAges: bio.household.kidsAges ?? "",
          }
        : null;
    setHousehold(nextHousehold);
    setWishList(bio.wishList ?? []);
    setNotes(bio.notes ?? "");
    setDesignerNotes(bio.designerNotes ?? "");
    hasHydratedBioRef.current = true;
  }, [bioData, isAddMode]);

  useEffect(() => {
    if (isAddMode) {
      return;
    }
    const image = bioData?.getHomeOwnerBio?.image;
    if (profileImage?.file) {
      return;
    }
    if (!image?.url) {
      if (profilePreview || profileImage) {
        setProfileImage(null);
        setProfilePreview(null);
      }
      setProfileImageRemoved(false);
      return;
    }
    if (profilePreview === image.url && profileImage?._id === image._id) {
      setProfileImageRemoved(false);
      return;
    }
    setProfileImage({
      _id: image._id ?? "",
      local_url: image.url ?? "",
    });
    setProfilePreview(image.url ?? null);
    setProfileImageRemoved(false);
  }, [
    bioData?.getHomeOwnerBio?.image?._id,
    bioData?.getHomeOwnerBio?.image?.url,
    isAddMode,
  ]);

  const projects = useMemo(
    () =>
      ((projectsData?.getHomeOwnerProjects ?? []).filter(
        Boolean
      ) as HomeownerProjectRecord[]),
    [projectsData]
  );

  const activeProjects = useMemo(() => {
    if (projectActiveFilter === true) {
      return projects.filter((project) => project.active !== false);
    }
    return projects;
  }, [projects, projectActiveFilter]);

  const applyProject = useCallback((project?: HomeownerProjectRecord | null) => {
    if (!project) {
      return;
    }
    setProjectId(project._id ?? null);
    setProjectSelection(project._id ?? "");
    const nextProjectName = project.projectName ?? "";
    setProjectName(nextProjectName);
    setProjectPropertyType(project.propertyType ?? "");
    setProjectScope(project.projectScope ?? []);
    setProjectBillingPreferences(project.billingPreferences ?? []);
    setProjectBudgetRange(project.budgetRange ?? "");
    setProjectSpecialRequirements(project.specialRequirements ?? []);
    setProjectFurniturePreferences(project.furniturePreferences ?? []);
    setProjectStartDate(project.projectStartDate || null);
    const nextHardDeadline = Boolean(project.hardDeadline);
    setHardDeadline(nextHardDeadline);
    const nextDeadline = project.deadlineDate || null;
    setDeadlineDate(nextHardDeadline ? ensureFutureDate(nextDeadline) : nextDeadline);
    setDeadlineDateWarning("");
    setFinancing(Boolean(project.financing));
    setReasonForHiring(normalizeReasonForHiring(project.reasonForHiring));
    setOtherReasonForHiring(project.otherReasonForHiring ?? "");
    setOwnershipDuration(project.ownershipDuration ?? "");
    setPropertyDetails(project.propertyDetails ?? "");
    setResidenceType(project.residenceType ?? "");
    setPropertyLocation(project.propertyLocation ?? "");
    setSquareFootage(project.squareFootage ?? "");
    setBedrooms(project.bedrooms ?? "");
    setBathrooms(project.bathrooms ?? "");
    setDecisionMakers(project.decisionMakers ?? "");
    setWorksRemotely(Boolean(project.worksRemotely));
    setWorkedWithDesigner(Boolean(project.workedWithDesigner));
    const normalizedRating = isExperienceRating(project.experience?.rating)
      ? project.experience?.rating
      : null;
    const normalizedNotes = project.experience?.notes ?? "";
    if (normalizedRating || normalizedNotes.trim()) {
      setExperience({
        rating: normalizedRating ?? "neutral",
        notes: normalizedNotes,
      });
    } else {
      setExperience(null);
    }
    setEntertainRegularly(Boolean(project.entertainRegularly));
    setHasPets(Boolean(project.hasPets));
    const nextPets = (project.pets ?? [])
      .map((pet) => ({
        type: pet.type ?? "",
        count: pet.count ?? "",
      }))
      .filter((pet) => pet.type || pet.count);
    setPets(project.hasPets ? nextPets : []);
    setHobbies(project.hobbies ?? []);
    const nextHousehold =
      project.household &&
      (project.household.adults ||
        project.household.kids ||
        project.household.kidsAges)
        ? {
            adults: project.household.adults ?? "",
            kids: project.household.kids ?? "",
            kidsAges: project.household.kidsAges ?? "",
          }
        : null;
    setHousehold(nextHousehold);
    setWishList(project.wishList ?? []);
    setNotes(project.notes ?? "");
    setDesignerNotes(project.designerNotes ?? "");
    setExperienceDraftRating(normalizedRating ?? "happy");
    setExperienceDraftNotes(normalizedNotes);
    setPetDrafts(project.hasPets ? nextPets : []);
    setHouseholdDraft({
      adults: nextHousehold?.adults ?? "",
      kids: nextHousehold?.kids ?? "",
      kidsAges: nextHousehold?.kidsAges ?? "",
    });
  }, [ensureFutureDate]);

  const handleProjectSelect = useCallback(
    (nextId: string) => {
      setProjectSelection(nextId);
      applyProject(activeProjects.find((project) => project._id === nextId));
    },
    [activeProjects, applyProject]
  );

  const handleHardDeadlineChange = useCallback(
    (value: boolean) => {
      setHardDeadline(value);
      setDeadlineDateWarning("");
      if (value) {
        const normalized = ensureFutureDate(deadlineDate) ?? getTomorrowDate();
        setDeadlineDate(normalized);
      }
    },
    [deadlineDate, ensureFutureDate, getTomorrowDate]
  );

  const handleDeadlineDateChange = useCallback(
    (value: string | null) => {
      if (!value) {
        setDeadlineDate(getTomorrowDate());
        setDeadlineDateWarning("");
        return;
      }
      const normalized = value.slice(0, 10);
      const minDate = getTomorrowDate();
      if (normalized < minDate) {
        setDeadlineDate(minDate);
        setDeadlineDateWarning(
          "Past or current dates aren't allowed. Date was set to tomorrow."
        );
        return;
      }
      setDeadlineDate(normalized);
      setDeadlineDateWarning("");
    },
    [getTomorrowDate]
  );

  useEffect(() => {
    if (isAddMode || hasHydratedProjectRef.current || !activeProjects.length) {
      return;
    }
    const selectedProject = initialProjectId
      ? activeProjects.find((project) => project._id === initialProjectId)
      : undefined;
    applyProject(selectedProject ?? activeProjects[0]);
    hasHydratedProjectRef.current = true;
  }, [activeProjects, applyProject, initialProjectId, isAddMode]);

  const hasMultipleProjects = !isAddMode && activeProjects.length > 1;

  const isMutating =
    isSaving ||
    createBioLoading ||
    updateBioLoading ||
    createProjectLoading ||
    updateProjectLoading ||
    profileImageLoading;

  const toFileList = useCallback((file: File) => {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    return dataTransfer.files;
  }, []);

  const handleSave = useCallback(
    async (action: "save" | "publish") => {
      if (!effectiveHomeOwnerId || isMutating || viewOnly) {
        return;
      }
      if (!decisionMakers.trim()) {
        showError("Please select the project decision maker(s).");
        return;
      }
      if (!projectName.trim()) {
        showError("Please enter a project name.");
        return;
      }
      if (!projectStartDate) {
        showError("Please select a project start date.");
        return;
      }
      if (reasonForHiring === OTHER_REASON_FOR_HIRING && !otherReasonForHiring.trim()) {
        showError("Please share your reason for hiring a designer.");
        return;
      }
      const shouldNavigateOnComplete = !bioId && !embedded;
      setIsSaving(true);
      try {
        const normalizedDeadlineDate = hardDeadline
          ? ensureFutureDate(deadlineDate) ?? getTomorrowDate()
          : deadlineDate;
        let uploadedProfileImageId = profileImage?._id ?? "";
        let uploadedProfileImageUrl = profileImage?.local_url ?? "";
        const shouldClearProfileImage =
          profileImageRemoved && !profileImage?.file;

        if (!embedded && profileImage?.file) {
          const uploaded = await uploadProfileImage(toFileList(profileImage.file));
          if (!uploaded?.length) {
            throw new Error("Failed to upload profile image");
          }
          uploadedProfileImageId = String(uploaded[0]._id ?? "");
          uploadedProfileImageUrl = (uploaded[0].url as string) ?? "";
          setProfileImage({
            _id: uploadedProfileImageId,
            local_url: uploadedProfileImageUrl,
          });
          setProfilePreview(uploadedProfileImageUrl || null);
          setProfileImageRemoved(false);
          if (typeof window !== "undefined" && uploadedProfileImageUrl) {
            localStorage.setItem("homeownerAvatarUrl", uploadedProfileImageUrl);
            window.dispatchEvent(
              new CustomEvent("homeowner-avatar-updated", {
                detail: uploadedProfileImageUrl,
              })
            );
          }
        }
        const normalizedPets = hasPets
          ? pets
              .map((pet) => ({
                type: pet.type.trim(),
                count: pet.count.trim(),
              }))
              .filter((pet) => pet.type || pet.count)
          : [];

        const normalizedWishList = wishList
          .map((item) => item.trim())
          .filter(Boolean);

        const normalizedHobbies = hobbies.map((item) => item.trim()).filter(Boolean);

        const normalizedHousehold =
          household &&
          (household.adults || household.kids || household.kidsAges)
            ? {
                adults: household.adults,
                kids: household.kids,
                kidsAges: household.kidsAges,
              }
            : null;

        const normalizedExperience =
          workedWithDesigner && experience
            ? {
                rating: experience.rating,
                notes: experience.notes.trim(),
              }
            : null;

        const otherReason =
          reasonForHiring === OTHER_REASON_FOR_HIRING
            ? otherReasonForHiring.trim()
            : "";

        if (!embedded) {
          if (bioId) {
            await updateHomeOwnerBioMutation({
              variables: {
                _id: bioId,
                hireLikelihood,
                decisionMakers,
                worksRemotely,
                workedWithDesigner,
                experience: normalizedExperience,
                entertainRegularly,
                hasPets,
                pets: normalizedPets,
                hobbies: normalizedHobbies,
                household: normalizedHousehold,
                wishList: normalizedWishList,
                notes,
                designerNotes,
                image: shouldClearProfileImage
                  ? null
                  : uploadedProfileImageId || undefined,
              },
            });
          } else {
            const result = await createHomeOwnerBioMutation({
              variables: {
                homeOwnerId: effectiveHomeOwnerId,
                hireLikelihood,
                decisionMakers,
                worksRemotely,
                workedWithDesigner,
                experience: normalizedExperience,
                entertainRegularly,
                hasPets,
                pets: normalizedPets,
                hobbies: normalizedHobbies,
                household: normalizedHousehold,
                wishList: normalizedWishList,
                notes,
                designerNotes,
                image: shouldClearProfileImage
                  ? null
                  : uploadedProfileImageId || undefined,
              },
            });
            const createdId = result.data?.createHomeOwnerBio?._id ?? null;
            if (createdId) {
              setBioId(createdId);
            }
          }

          if (profileImage?.file && uploadedProfileImageId) {
            await updateHomeownerAccountData({
              _id: effectiveHomeOwnerId,
              image: uploadedProfileImageId,
            });
          } else if (shouldClearProfileImage) {
            await updateHomeownerAccountData({
              _id: effectiveHomeOwnerId,
              image: null,
            });
            if (typeof window !== "undefined") {
              localStorage.removeItem("homeownerAvatarUrl");
              window.dispatchEvent(
                new CustomEvent("homeowner-avatar-updated", { detail: null })
              );
            }
          }
        }

        const projectPayload = {
          projectName,
          projectStartDate: projectStartDate ?? "",
          hardDeadline,
          deadlineDate: hardDeadline ? normalizedDeadlineDate ?? "" : "",
          financing,
          reasonForHiring,
          otherReasonForHiring: otherReason,
          ownershipDuration,
          propertyDetails,
          residenceType,
          propertyLocation,
          squareFootage,
          bedrooms,
          bathrooms,
        };

        if (embedded) {
          Object.assign(projectPayload, {
            propertyType: projectPropertyType,
            projectScope,
            billingPreferences: projectBillingPreferences,
            budgetRange: projectBudgetRange,
            specialRequirements: projectSpecialRequirements,
            furniturePreferences: projectFurniturePreferences,
            decisionMakers,
            worksRemotely,
            workedWithDesigner,
            experience: normalizedExperience,
            entertainRegularly,
            hasPets,
            pets: normalizedPets,
            hobbies: normalizedHobbies,
            household: normalizedHousehold,
            wishList: normalizedWishList,
            notes,
            designerNotes,
          });
        }

        if (projectId) {
          await updateHomeOwnerProjectMutation({
            variables: {
              _id: projectId,
              ...projectPayload,
            },
          });
        } else {
          const result = await createHomeOwnerProjectMutation({
            variables: {
              homeOwnerId: effectiveHomeOwnerId,
              active: true,
              ...projectPayload,
            },
          });
          const createdId = result.data?.createHomeOwnerProject?._id ?? null;
          if (createdId) {
            setProjectId(createdId);
            setProjectSelection(createdId);
          }
        }

        showSuccess(
          action === "publish"
            ? "Bio published successfully."
            : "Changes saved successfully."
        );
        if (!embedded) {
          await refetchBio();
        }
        if (embedded) {
          onComplete?.();
        } else if (action === "publish" || shouldNavigateOnComplete) {
          navigate(Pages.YOUR_HUB);
        }
      } catch (error) {
        const message =
          (error as { graphQLErrors?: Array<{ message?: string }> })?.graphQLErrors?.[0]?.message ||
          (error as Error)?.message ||
          "We couldn’t save your changes. Please try again.";
        showError(message);
      } finally {
        setIsSaving(false);
      }
    },
    [
      effectiveHomeOwnerId,
      isMutating,
      viewOnly,
      hasPets,
      pets,
      wishList,
      hobbies,
      household,
      workedWithDesigner,
      experience,
      reasonForHiring,
      otherReasonForHiring,
      bioId,
      hireLikelihood,
      decisionMakers,
      worksRemotely,
      entertainRegularly,
      notes,
      designerNotes,
      projectId,
      projectName,
      projectPropertyType,
      projectScope,
      projectBillingPreferences,
      projectBudgetRange,
      projectSpecialRequirements,
      projectFurniturePreferences,
      projectStartDate,
      hardDeadline,
      deadlineDate,
      financing,
      ownershipDuration,
      propertyDetails,
      residenceType,
      propertyLocation,
      squareFootage,
      bedrooms,
      bathrooms,
      profileImage,
      profileImageRemoved,
      uploadProfileImage,
      toFileList,
      updateHomeownerAccountData,
      createHomeOwnerBioMutation,
      updateHomeOwnerBioMutation,
      createHomeOwnerProjectMutation,
      updateHomeOwnerProjectMutation,
      ensureFutureDate,
      getTomorrowDate,
      showError,
      showSuccess,
      refetchBio,
      navigate,
      embedded,
      onComplete,
    ]
  );

  const handlePublish = useCallback(() => {
    void handleSave("publish");
  }, [handleSave]);

  const handleSaveChanges = useCallback(() => {
    void handleSave("save");
  }, [handleSave]);

  const handleViewAsDesigner = useCallback(() => {
    if (!effectiveHomeOwnerId) {
      return;
    }
    navigate(
      Pages.HOMEOWNER_PROFILE.replace(
        ":homeOwnerId",
        effectiveHomeOwnerId
      )
    );
  }, [navigate, effectiveHomeOwnerId]);

  const renderActionButtons = useCallback(
    () => (
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ width: "100%" }}
      >
        <Button
          variant="outlined"
          onClick={handlePublish}
          disabled={isMutating}
          startIcon={
            isMutating ? (
              <CircularProgress size={18} sx={{ color: "#1C1C1C" }} />
            ) : undefined
          }
          sx={{
            textTransform: "none",
            borderColor: "#1C1C1C",
            color: "#1C1C1C",
            borderRadius: "10px",
            px: 4,
            minWidth: 140,
            height: 40,
            fontWeight: 600,
          }}
        >
          Publish
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveChanges}
          disabled={isMutating}
          startIcon={
            isMutating ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : undefined
          }
          sx={{
            textTransform: "none",
            backgroundColor: "#1C1C1C",
            borderRadius: "10px",
            px: 4,
            minWidth: 160,
            height: 40,
            fontWeight: 600,
            "&:hover": { backgroundColor: "#1C1C1C" },
          }}
        >
          Save Changes
        </Button>
      </Stack>
    ),
    [handlePublish, handleSaveChanges, isMutating]
  );

  useEffect(() => {
    if (viewOnly || embedded) {
      clearFooter();
      return;
    }
    setFooter(renderActionButtons());
    return () => clearFooter();
  }, [
    setFooter,
    clearFooter,
    renderActionButtons,
    viewOnly,
    embedded,
  ]);

  const profile = matchingData?.getHomeOwnerMatchingProfile ?? null;
  const preferences = profile?.preferences;
  const matchingProject = profile?.projectType;

  const isRentalProject = useMemo(() => {
    const projectType = isProjectModal
      ? projectPropertyType
      : matchingProject?.projectType;
    if (!projectType) return false;
    const isRentalValue = (value: string) => {
      const normalized = value
        .replace(/[^a-z0-9]/gi, "")
        .toLowerCase();
      return normalized.includes("rentalproperty") || normalized.includes("airbnb");
    };
    if (Array.isArray(projectType)) {
      return projectType.some((value) => value && isRentalValue(String(value)));
    }
    return isRentalValue(String(projectType));
  }, [isProjectModal, matchingProject?.projectType, projectPropertyType]);
  const showPropertyDetails = isRentalProject;

  const displayName = useMemo(() => {
    const name = [user?.firstName, user?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    return name || "Your";
  }, [user?.firstName, user?.lastName]);

  const headerTitle = viewOnly ? "Homeowner Profile" : "My Bio";

  const personalityType = useMemo(
    () =>
      formatValue(profile?.personalityType, "Extrovert Blue Orange"),
    [profile?.personalityType]
  );

  const topMatchingAttributes = useMemo(() => {
    const fromState = (location.state as { topMatchTags?: string[] } | null)
      ?.topMatchTags;
    const normalizedStateTags = Array.isArray(fromState)
      ? fromState.filter((tag) => typeof tag === "string" && tag.trim()).slice(0, 3)
      : [];
    if (normalizedStateTags.length) return normalizedStateTags;

    const order: string[] = profile?.priorities?.order ?? [];
    const labels = order
      .map((key) => PRIORITY_LABELS[key] || toTitleCase(key))
      .filter(Boolean)
      .slice(0, 3);
    return labels.length ? labels : ["Budget", "Style", "Color"];
  }, [location.state, profile?.priorities?.order]);

  const partnershipStyle = useMemo(
    () =>
      formatValue(
        preferences?.partnershipStyle ? toTitleCase(preferences.partnershipStyle) : "",
        "Designer Full Service"
      ),
    [preferences?.partnershipStyle]
  );

  const topDesignerSkills = useMemo(() => {
    const skills: string[] = preferences?.skillRankings ?? [];
    const formatted = skills.map((skill) => toTitleCase(skill)).slice(0, 3);
    return formatted.length
      ? formatted
      : ["Budget Management", "Creativity", "Problem Solving"];
  }, [preferences?.skillRankings]);

  const projectTypeLabels = useMemo(() => {
    if (isAddMode) {
      return [];
    }
    const projectTypeValue = matchingProject?.projectType
      ? [matchingProject.projectType]
      : [];
    return formatList(projectTypeValue, [
      "Living Room",
      "Color Consulting",
      "Furniture Selection",
    ]);
  }, [matchingProject?.projectType, isAddMode]);

  const projectScopeLabels = useMemo(() => {
    if (isAddMode) {
      return [];
    }
    return formatList(
      matchingProject?.designItems as (string | null | undefined)[] | undefined
    );
  }, [matchingProject?.designItems, isAddMode]);

  const billingPreferences = useMemo(() => {
    if (isAddMode) {
      return [];
    }
    return formatList(preferences?.billingMethods ?? [], ["Flat Rate"]);
  }, [preferences?.billingMethods, isAddMode]);

  const budgetRange = useMemo(() => {
    if (isAddMode) {
      return "";
    }
    return formatValue(preferences?.budgetRange, "$15,001 - $20,000");
  }, [preferences?.budgetRange, isAddMode]);

  const specialRequirements = useMemo(() => {
    if (isAddMode) {
      return [];
    }
    return formatList(
      matchingProject?.specialNeeds as (string | null | undefined)[] | undefined,
      ["Handicap Accessible", "Pet Friendly Design", "Smart Home Integration"]
    );
  }, [matchingProject?.specialNeeds, isAddMode]);

  const displayFurniturePreferences = useMemo(() => {
    if (isAddMode) {
      return [];
    }
    const values = [
      preferences?.designerRole,
      preferences?.acquisitionPreference,
    ].filter(Boolean) as string[];
    return formatList(values, ["Designer selects, buys and handles all"]);
  }, [preferences?.designerRole, preferences?.acquisitionPreference, isAddMode]);

  const furniturePreferences = isProjectModal
    ? projectFurniturePreferences
    : displayFurniturePreferences;

  const handleProfileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showError("File size must be less than 5MB");
      return;
    }
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      showError("Please upload a JPG or PNG file");
      return;
    }
    const localUrl = URL.createObjectURL(file);
    setProfileImage({
      file,
      local_url: localUrl,
    });
    setProfilePreview(localUrl);
    setProfileImageRemoved(false);
    event.target.value = "";
  };

  const handleProfileRemove = () => {
    setProfileImage(null);
    setProfilePreview(null);
    setProfileImageRemoved(true);
    if (typeof window !== "undefined") {
      localStorage.removeItem("homeownerAvatarUrl");
      window.dispatchEvent(
        new CustomEvent("homeowner-avatar-updated", { detail: null })
      );
    }
  };

  const openExperienceModal = () => {
    setExperienceDraftRating(experience?.rating ?? "happy");
    setExperienceDraftNotes(experience?.notes ?? "");
    setExperienceModalOpen(true);
  };

  const saveExperience = () => {
    setExperience({
      rating: experienceDraftRating,
      notes: experienceDraftNotes.trim(),
    });
    setExperienceModalOpen(false);
  };

  const openPetsModal = () => {
    setPetDrafts(pets.length ? pets : [{ type: "", count: "1" }]);
    setPetsModalOpen(true);
  };

  const savePets = () => {
    const cleaned = petDrafts.filter(
      (pet) => pet.type.trim() && pet.count.trim()
    );
    setPets(cleaned);
    setPetsModalOpen(false);
  };

  const openHouseholdModal = () => {
    setHouseholdDraft(
      household ?? {
        adults: "",
        kids: "",
        kidsAges: "",
      }
    );
    setHouseholdModalOpen(true);
  };

  const saveHousehold = () => {
    setHousehold(householdDraft);
    setHouseholdModalOpen(false);
  };

  const adultsCount = parseCountValue(householdDraft.adults);
  const kidsCount = parseCountValue(householdDraft.kids);
  const kidsAgeValues = useMemo(() => {
    if (kidsCount <= 0) {
      return [];
    }
    const ages = householdDraft.kidsAges
      .split(",")
      .map((age) => age.trim())
      .filter(Boolean);
    return Array.from({ length: kidsCount }, (_, index) =>
      parseCountValue(ages[index] ?? "0")
    );
  }, [householdDraft.kidsAges, kidsCount]);

  const adjustHouseholdCount = (
    key: "adults" | "kids",
    delta: number,
    min = 0
  ) => {
    setHouseholdDraft((prev) => {
      const current = parseCountValue(prev[key]);
      const next = Math.max(min, current + delta);
      if (key !== "kids") {
        return { ...prev, [key]: String(next) };
      }
      const ages = prev.kidsAges
        .split(",")
        .map((age) => age.trim())
        .filter(Boolean);
      const normalized = Array.from(
        { length: next },
        (_, index) => ages[index] ?? "0"
      );
      return {
        ...prev,
        kids: String(next),
        kidsAges: normalized.join(", "),
      };
    });
  };

  const adjustKidsAge = (index: number, delta: number) => {
    setHouseholdDraft((prev) => {
      const kidsTotal = parseCountValue(prev.kids);
      if (kidsTotal <= 0) {
        return prev;
      }
      const ages = prev.kidsAges
        .split(",")
        .map((age) => age.trim())
        .filter(Boolean);
      const normalized = Array.from(
        { length: kidsTotal },
        (_, idx) => ages[idx] ?? "0"
      );
      const current = parseCountValue(normalized[index]);
      const next = Math.max(0, current + delta);
      normalized[index] = String(next);
      return { ...prev, kidsAges: normalized.join(", ") };
    });
  };

  const handleAddWishItem = () => {
    const nextItem = newWishItem.trim();
    if (!nextItem) {
      return;
    }
    setWishList((prev) => [...prev, nextItem]);
    setNewWishItem("");
  };

  return (
    <Box>
      {!embedded && (
        <DashboardHeader
          title={headerTitle}
          primaryAction={
            viewOnly
              ? undefined
              : {
                  label: "View as Designer",
                  onClick: handleViewAsDesigner,
                  variant: "outlined",
                }
          }
        />
      )}
      <Box
        sx={{
          width: "100%",
          px: 3,
          pb: 6,
          pointerEvents: viewOnly ? "none" : "auto",
        }}
      >
        <Stack spacing={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: CARD_RADIUS,
              border: CARD_BORDER,
              backgroundColor: "#fff",
            }}
          >
            <Stack spacing={3}>
              {!embedded && (
                <BioHeaderSection
                  displayName={displayName}
                  profilePreview={profilePreview}
                  onProfileUpload={handleProfileUpload}
                  onProfileRemove={handleProfileRemove}
                  homeownerUser={homeownerUser}
                  personalityType={personalityType}
                  partnershipStyle={partnershipStyle}
                  topMatchingAttributes={topMatchingAttributes}
                  topDesignerSkills={topDesignerSkills}
                  hireLikelihood={hireLikelihood}
                  onHireLikelihoodChange={setHireLikelihood}
                />
              )}
              <Box
                sx={{
                  border: SECTION_BORDER,
                  borderRadius: SECTION_RADIUS,
                  p: 4,
                }}
              >
                <Stack spacing={3}>
                  <ProjectSection
                    hasMultipleProjects={hasMultipleProjects}
                    activeProjects={activeProjects}
                    projectSelection={projectSelection}
                    projectId={projectId}
                    onProjectSelect={handleProjectSelect}
                    projectName={projectName}
                    onProjectNameChange={setProjectName}
                    projectTypeLabels={projectTypeLabels}
                    projectScopeLabels={projectScopeLabels}
                    projectStartDate={projectStartDate}
                    onProjectStartDateChange={setProjectStartDate}
                    billingPreferences={billingPreferences}
                    budgetRange={budgetRange}
                    hardDeadline={hardDeadline}
                    onHardDeadlineChange={handleHardDeadlineChange}
                    deadlineDate={deadlineDate}
                    onDeadlineDateChange={handleDeadlineDateChange}
                    isDeadlineDateValid={!deadlineDateWarning}
                    deadlineDateValidationText={deadlineDateWarning}
                    financing={financing}
                    onFinancingChange={setFinancing}
                    specialRequirements={specialRequirements}
                    isEditable={isProjectModal}
                    projectPropertyType={projectPropertyType}
                    onProjectPropertyTypeChange={setProjectPropertyType}
                    projectScope={projectScope}
                    onProjectScopeChange={setProjectScope}
                    projectBillingPreferences={projectBillingPreferences}
                    onProjectBillingPreferencesChange={setProjectBillingPreferences}
                    projectBudgetRange={projectBudgetRange}
                    onProjectBudgetRangeChange={setProjectBudgetRange}
                    projectSpecialRequirements={projectSpecialRequirements}
                    onProjectSpecialRequirementsChange={setProjectSpecialRequirements}
                    reasonForHiring={reasonForHiring}
                    onReasonForHiringChange={setReasonForHiring}
                    otherReasonForHiring={otherReasonForHiring}
                    onOtherReasonForHiringChange={setOtherReasonForHiring}
                    ownershipDuration={ownershipDuration}
                    onOwnershipDurationChange={setOwnershipDuration}
                    propertyDetails={propertyDetails}
                    onPropertyDetailsChange={setPropertyDetails}
                    showPropertyDetails={showPropertyDetails}
                    residenceType={residenceType}
                    onResidenceTypeChange={setResidenceType}
                    propertyLocation={propertyLocation}
                    onPropertyLocationChange={setPropertyLocation}
                    squareFootage={squareFootage}
                    onSquareFootageChange={setSquareFootage}
                    bedrooms={bedrooms}
                    onBedroomsChange={setBedrooms}
                    bathrooms={bathrooms}
                    onBathroomsChange={setBathrooms}
                  />
                  <WorkStyleSection
                    furniturePreferences={furniturePreferences}
                    isEditableFurniturePreferences={isProjectModal}
                    onFurniturePreferencesChange={setProjectFurniturePreferences}
                    isRentalProject={isRentalProject}
                    decisionMakers={decisionMakers}
                    onDecisionMakersChange={setDecisionMakers}
                    worksRemotely={worksRemotely}
                    onWorksRemotelyChange={setWorksRemotely}
                    workedWithDesigner={workedWithDesigner}
                    onWorkedWithDesignerChange={setWorkedWithDesigner}
                    experience={experience}
                    onEditExperience={openExperienceModal}
                    entertainRegularly={entertainRegularly}
                    onEntertainRegularlyChange={setEntertainRegularly}
                    hasPets={hasPets}
                    onHasPetsChange={setHasPets}
                    pets={pets}
                    onEditPets={openPetsModal}
                    hobbies={hobbies}
                    onHobbiesChange={setHobbies}
                    household={household}
                    onEditHousehold={openHouseholdModal}
                  />
                  <AdditionalDetailsSection
                    wishList={wishList}
                    onRemoveWishItem={(item) =>
                      setWishList((prev) => prev.filter((wish) => wish !== item))
                    }
                    newWishItem={newWishItem}
                    onNewWishItemChange={setNewWishItem}
                    onAddWishItem={handleAddWishItem}
                    notes={notes}
                    onNotesChange={setNotes}
                    designerNotes={designerNotes}
                    onDesignerNotesChange={setDesignerNotes}
                  />
                </Stack>
              </Box>
            </Stack>
          </Paper>
          {embedded && !viewOnly && (
            <Box sx={{ mt: 3 }}>{renderActionButtons()}</Box>
          )}
        </Stack>
      </Box>

      <ExperienceDialog
        open={experienceModalOpen}
        onClose={() => setExperienceModalOpen(false)}
        rating={experienceDraftRating}
        onRatingChange={setExperienceDraftRating}
        notes={experienceDraftNotes}
        onNotesChange={setExperienceDraftNotes}
        onSave={saveExperience}
        hasExperience={Boolean(experience)}
      />
      <PetsDialog
        open={petsModalOpen}
        onClose={() => setPetsModalOpen(false)}
        petDrafts={petDrafts}
        onPetDraftsChange={setPetDrafts}
        onSave={savePets}
      />
      <HouseholdDialog
        open={householdModalOpen}
        onClose={() => setHouseholdModalOpen(false)}
        adultsCount={adultsCount}
        kidsCount={kidsCount}
        kidsAgeValues={kidsAgeValues}
        onAdjustHouseholdCount={adjustHouseholdCount}
        onAdjustKidsAge={adjustKidsAge}
        onSave={saveHousehold}
      />
    </Box>
  );
};

export default HomeownerBioPage;
